import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';
import TextAreaField from '../../../../components/wizard/TextAreaField';
import { WizardData } from '../../../../types/wizard';

interface Step9SpecialNeedsProps {
  data: WizardData;
  onChange: (field: keyof WizardData, value: any) => void;
  onAdvance: () => void;
  error?: string;
}

const dietaryRestrictions = [
  { id: 'vegetariano', label: 'Vegetariano', icon: 'ri-leaf-line' },
  { id: 'vegano', label: 'Vegano', icon: 'ri-plant-line' },
  { id: 'sem_gluten', label: 'Sem Glúten', icon: 'ri-wheat-line' },
  { id: 'sem_lactose', label: 'Sem Lactose', icon: 'ri-drop-line' },
  { id: 'halal', label: 'Halal', icon: 'ri-restaurant-line' },
  { id: 'kosher', label: 'Kosher', icon: 'ri-restaurant-2-line' },
  { id: 'alergias', label: 'Alergias Alimentares', icon: 'ri-alert-line' }
];

const mobilityOptions = [
  { id: 'cadeira_rodas', label: 'Cadeira de Rodas', icon: 'ri-wheelchair-line' },
  { id: 'mobilidade_reduzida', label: 'Mobilidade Reduzida', icon: 'ri-walk-line' },
  { id: 'bengala', label: 'Uso de Bengala', icon: 'ri-user-line' }
];

const healthConditions = [
  { id: 'diabetes', label: 'Diabetes', icon: 'ri-heart-pulse-line' },
  { id: 'hipertensao', label: 'Hipertensão', icon: 'ri-heart-line' },
  { id: 'asma', label: 'Asma', icon: 'ri-lungs-line' },
  { id: 'medicacao_continua', label: 'Medicação Contínua', icon: 'ri-capsule-line' }
];

export default function Step9SpecialNeeds({ data, onChange, onAdvance, error }: Step9SpecialNeedsProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const [selectedDietary, setSelectedDietary] = useState<string[]>(data.dietaryRestrictions || []);
  const [selectedMobility, setSelectedMobility] = useState<string[]>(data.mobilityNeeds || []);
  const [selectedHealth, setSelectedHealth] = useState<string[]>(data.healthConditions || []);
  const [additionalNotes, setAdditionalNotes] = useState(data.specialNeedsNotes || '');

  const handleDietaryToggle = (id: string) => {
    const updated = selectedDietary.includes(id)
      ? selectedDietary.filter(item => item !== id)
      : [...selectedDietary, id];
    setSelectedDietary(updated);
    onChange('dietaryRestrictions', updated);
    onChange('specialNeedsStatus', 'provided');
  };

  const handleMobilityToggle = (id: string) => {
    const updated = selectedMobility.includes(id)
      ? selectedMobility.filter(item => item !== id)
      : [...selectedMobility, id];
    setSelectedMobility(updated);
    onChange('mobilityNeeds', updated);
    onChange('specialNeedsStatus', 'provided');
  };

  const handleHealthToggle = (id: string) => {
    const updated = selectedHealth.includes(id)
      ? selectedHealth.filter(item => item !== id)
      : [...selectedHealth, id];
    setSelectedHealth(updated);
    onChange('healthConditions', updated);
    onChange('specialNeedsStatus', 'provided');
  };

  const handleNotesChange = (value: string) => {
    setAdditionalNotes(value);
    onChange('specialNeedsNotes', value);
    onChange('specialNeedsStatus', value.trim() ? 'provided' : 'not_answered');
  };

  const clearLocalSelections = () => {
    setSelectedDietary([]);
    setSelectedMobility([]);
    setSelectedHealth([]);
    setAdditionalNotes('');
  };

  const handleClear = () => {
    clearLocalSelections();
    onChange('dietaryRestrictions', []);
    onChange('mobilityNeeds', []);
    onChange('healthConditions', []);
    onChange('specialNeedsNotes', '');
    onChange('specialNeedsStatus', 'not_answered');
  };

  const handleSkip = () => {
    clearLocalSelections();
    onAdvance();
  };

  const totalSelected = selectedDietary.length + selectedMobility.length + selectedHealth.length;
  const localizedDietaryRestrictions = dietaryRestrictions.map((item) => ({
    ...item,
    label:
      isEnglish
        ? ({
            vegetariano: 'Vegetarian',
            vegano: 'Vegan',
            sem_gluten: 'Gluten-free',
            sem_lactose: 'Lactose-free',
            halal: 'Halal',
            kosher: 'Kosher',
            alergias: 'Food Allergies',
          } as Record<string, string>)[item.id]
        : item.label,
  }));
  const localizedMobilityOptions = mobilityOptions.map((item) => ({
    ...item,
    label:
      isEnglish
        ? ({
            cadeira_rodas: 'Wheelchair',
            mobilidade_reduzida: 'Reduced Mobility',
            bengala: 'Cane Support',
          } as Record<string, string>)[item.id]
        : item.label,
  }));
  const localizedHealthConditions = healthConditions.map((item) => ({
    ...item,
    label:
      isEnglish
        ? ({
            diabetes: 'Diabetes',
            hipertensao: 'High Blood Pressure',
            asma: 'Asthma',
            medicacao_continua: 'Continuous Medication',
          } as Record<string, string>)[item.id]
        : item.label,
  }));

  return (
    <StepCard
      title={isEnglish ? 'Special Needs' : 'Necessidades Especiais'}
      description={isEnglish
        ? 'Share any dietary restrictions, mobility needs or health conditions we should consider. This step is optional and confidential.'
        : 'Compartilhe informações sobre restrições alimentares, mobilidade ou condições de saúde que devemos considerar. Esta etapa é opcional e confidencial.'}
    >
      <div className="space-y-8">
        {/* Summary Counter */}
        <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-lg">
          <span className="text-sm text-white/90">{isEnglish ? 'Needs informed' : 'Necessidades informadas'}</span>
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-white/90">
              {totalSelected}
            </span>
            {totalSelected > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-white/90 hover:text-white transition-colors whitespace-nowrap"
              >
                {isEnglish ? 'Clear all' : 'Limpar tudo'}
              </button>
            )}
          </div>
        </div>

        {/* Dietary Restrictions */}
        <div>
          <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
            <i className="ri-restaurant-line text-[#FFD700]"></i>
            {isEnglish ? 'Dietary Restrictions' : 'Restrições Alimentares'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {localizedDietaryRestrictions.map((item) => {
              const isSelected = selectedDietary.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleDietaryToggle(item.id)}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 whitespace-nowrap
                    ${isSelected
                      ? 'bg-[#FFD700]/10 border-[#FFD700] text-white'
                      : 'bg-white/5 border-white/10 text-white/90 hover:bg-white/10 hover:border-white/20'
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className={`${item.icon} text-xl ${isSelected ? 'text-[#FFD700]' : 'text-white/90'}`}></i>
                    <span className="text-xs font-medium">{item.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobility Needs */}
        <div>
          <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
            <i className="ri-wheelchair-line text-[#FFD700]"></i>
            {isEnglish ? 'Mobility' : 'Mobilidade'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {localizedMobilityOptions.map((item) => {
              const isSelected = selectedMobility.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleMobilityToggle(item.id)}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 whitespace-nowrap
                    ${isSelected
                      ? 'bg-[#FFD700]/10 border-[#FFD700] text-white'
                      : 'bg-white/5 border-white/10 text-white/90 hover:bg-white/10 hover:border-white/20'
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className={`${item.icon} text-xl ${isSelected ? 'text-[#FFD700]' : 'text-white/90'}`}></i>
                    <span className="text-xs font-medium">{item.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Health Conditions */}
        <div>
          <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
            <i className="ri-heart-pulse-line text-[#FFD700]"></i>
            {isEnglish ? 'Health Conditions' : 'Condições de Saúde'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {localizedHealthConditions.map((item) => {
              const isSelected = selectedHealth.includes(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleHealthToggle(item.id)}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 whitespace-nowrap
                    ${isSelected
                      ? 'bg-[#FFD700]/10 border-[#FFD700] text-white'
                      : 'bg-white/5 border-white/10 text-white/90 hover:bg-white/10 hover:border-white/20'
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <i className={`${item.icon} text-xl ${isSelected ? 'text-[#FFD700]' : 'text-white/90'}`}></i>
                    <span className="text-xs font-medium">{item.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Additional Notes */}
        <div>
          <h3 className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
            <i className="ri-file-text-line text-[#FFD700]"></i>
            {isEnglish ? 'Additional Information (Optional)' : 'Informações Adicionais (Opcional)'}
          </h3>
          <TextAreaField
            label={isEnglish ? 'Additional information' : 'Informações adicionais'}
            value={additionalNotes}
            onChange={handleNotesChange}
            placeholder={
              isEnglish
                ? 'Describe any other special need, allergy, medication that requires refrigeration, or any information that could help us plan your trip better...'
                : 'Descreva qualquer outra necessidade especial, alergia específica, medicação que precisa de refrigeração, ou qualquer informação que possa nos ajudar a planejar melhor sua viagem...'
            }
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-white/90 mt-2">
            {additionalNotes.length}/500 {isEnglish ? 'characters' : 'caracteres'}
          </p>
        </div>

        {/* Skip Option */}
        {totalSelected === 0 && !additionalNotes && (
          <div className="text-center py-6">
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-[#FFD700] hover:text-[#FFD700]/80 transition-colors whitespace-nowrap"
            >
              {isEnglish ? 'I do not have special needs →' : 'Não tenho necessidades especiais →'}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <i className="ri-error-warning-line text-red-400"></i>
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        {/* Privacy Notice */}
        <div className="p-4 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg">
          <div className="flex gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-lock-line text-[#FFD700]"></i>
            </div>
            <div className="text-xs text-white/90 leading-relaxed">
              <strong className="text-white/90">{isEnglish ? 'Privacy:' : 'Privacidade:'}</strong>{' '}
              {isEnglish
                ? 'All health information is treated confidentially and used exclusively to personalize your itinerary and help ensure your safety and comfort during the trip.'
                : 'Todas as informações de saúde são tratadas com confidencialidade e usadas exclusivamente para personalizar seu roteiro e garantir sua segurança e conforto durante a viagem.'}
            </div>
          </div>
        </div>
      </div>
    </StepCard>
  );
}
